import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import clsx from 'clsx';
import { Button, DisplayMessage, Icon, Tooltip, Typography } from '@gbm/queen-ui-guidelines';

import IconDelete from 'components/IconDelete';
import { NavLink } from 'react-router-dom';
import { ApplicationModal } from './ApplicationModal';

import styles from './styles.module.scss';
import { ApplicationDeleteDialog } from '../Dialogs/ApplicationDeleteDialog';
import { ApplicationDeleteConfirmation } from '../Dialogs/ApplicationDeleteConfirmation';

import { useDispatcherApplication } from 'providers/AccessManagementApplication/useDispatcher';
import { useSelectorApplication } from 'providers/AccessManagementApplication/useSelectors';

function Applications({ applications, small }) {
  const [isOpenModalApp, setIsOpenModalApp] = useState(false);
  const [isOpenDialogDeleteApp, setIsOpenDialogDeleteApp] = useState(false);
  const [isOpenConfirmationDeleteApp, setIsOpenConfirmationDeleteApp] = useState(false);
  const [appToEdit, setAppToEdit] = useState(null);
  const [appToDelete, setAppToDelete] = useState(null);
  const { applicationsState, selectedApplicationState } = useSelectorApplication();
  const activeAppId = selectedApplicationState.app?.sk ?? null;

  const {
    getApplications,
    createApplication,
    updateApplication,
    deleteApplication,
    selectedApplication,
    resetCreateApplication,
    resetUpdateApplication,
    resetDeleteApplication,
  } = useDispatcherApplication();

  const { createApplicationState, updateApplicationState, deleteApplicationState } = useSelectorApplication();

  useEffect(() => {
    return () => {
      resetCreateApplication();
      resetUpdateApplication();
      resetDeleteApplication();
    };
  }, [resetCreateApplication, resetUpdateApplication, resetDeleteApplication]);

  const handleOpenModalCreateApp = () => {
    setIsOpenModalApp(true);
    setAppToEdit(null);
  };

  const handleOpenModalToEditApp = app => {
    setIsOpenModalApp(true);
    setAppToEdit(app);
  };

  const handleCloseModalApp = () => {
    setIsOpenModalApp(false);
    setAppToEdit(null);
    resetCreateApplication();
    resetUpdateApplication();
  };

  const handleOpenDialogToDeleteApp = app => {
    setAppToDelete(app);
    setIsOpenDialogDeleteApp(true);
  };

  const handleOpenConfirmationDeleteApp = e => {
    e.preventDefault();
    setIsOpenDialogDeleteApp(false);
    setIsOpenConfirmationDeleteApp(true);
  };

  const handleCloseConfirmationDeleteApp = () => {
    setIsOpenConfirmationDeleteApp(false);
  };

  const handleCreateApp = ({ clientId, app, displayName, type }) => {
    createApplication({ clientId, app, displayName, type: type?.value || type });
  };

  const handleEditApp = ({ displayName }) => {
    updateApplication(appToEdit.id, { displayName });
  };

  const handleAppSelected = app => {
    selectedApplication(app);
  };

  useEffect(() => {
    if (createApplicationState.loaded || updateApplicationState.loaded) {
      setIsOpenModalApp(false);
      getApplications();
    }
  }, [createApplicationState.loaded, updateApplicationState.loaded, getApplications]);

  useEffect(() => {
    if (deleteApplicationState.loaded) {
      setIsOpenConfirmationDeleteApp(false);
      getApplications();
    }
  }, [deleteApplicationState.loaded, getApplications]);

  const renderListApplications = () => {
    if (!applications.length) {
      return (
        <DisplayMessage id="msgApplicationsEmptyInfo" variant="info" message="There are not applications configured" />
      );
    }
    const visibleApps = applications.filter(app => app.to !== undefined && app.hasPermission);

    return (
      <ul className="nav">
        {visibleApps.map((app, i) => (
          <li key={app.sk ?? i} className={clsx(styles.sidebarLabel, { [styles.sidebarLabelSmall]: small })}>
            <NavLink
              exact
              to={app.to}
              activeClassName={activeAppId === app.sk ? 'active' : 'inactive'}
              className={styles.content}
              title={app.application}
              onClick={() => handleAppSelected(app)}>
              {app.application}
            </NavLink>
            <div className={styles.actions}>
              <Tooltip message="Edit" position="top">
                <Button
                  id={'btnEditApp'}
                  data-testid={'btnEditApp'}
                  icon
                  size="xs"
                  color="light"
                  onClick={e => {
                    e.stopPropagation();
                    handleOpenModalToEditApp(app);
                  }}>
                  <Icon className="remixicon-pencil-fill" />
                </Button>
              </Tooltip>
              <Tooltip message="Delete" position="top">
                <Button
                  id={'btnDeleteApp'}
                  data-testid={'btnDeleteApp'}
                  icon
                  size="xs"
                  color="light"
                  onClick={e => {
                    e.stopPropagation();
                    handleOpenDialogToDeleteApp(app);
                  }}>
                  <IconDelete />
                </Button>
              </Tooltip>
            </div>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <>
      <div className={styles.sidebarTitle}>
        <Typography component="h4">{'Applications'}</Typography>
        <Button bordered={false} color="info" icon size={'xs'} type="button" onClick={handleOpenModalCreateApp}>
          <Icon className="fas fa-plus" component="i" />
        </Button>
      </div>
      {renderListApplications()}

      {isOpenModalApp && (
        <ApplicationModal
          open={isOpenModalApp}
          onClose={handleCloseModalApp}
          title={appToEdit ? 'Edit application' : 'Create application'}
          applications={applicationsState.list}
          appToEdit={appToEdit}
          onSubmit={appToEdit ? handleEditApp : handleCreateApp}
          loading={appToEdit ? updateApplicationState.loading : createApplicationState.loading}
          error={Boolean(appToEdit ? updateApplicationState.error : createApplicationState.error)}
        />
      )}

      {isOpenDialogDeleteApp && (
        <ApplicationDeleteDialog
          isOpen={isOpenDialogDeleteApp}
          onClose={() => setIsOpenDialogDeleteApp(false)}
          onConfirm={handleOpenConfirmationDeleteApp}
          application={appToDelete}
        />
      )}

      {isOpenConfirmationDeleteApp && (
        <ApplicationDeleteConfirmation
          isOpen={isOpenConfirmationDeleteApp}
          onClose={handleCloseConfirmationDeleteApp}
          onConfirm={() => deleteApplication(appToDelete.id)}
          loading={deleteApplicationState.loading}
          error={deleteApplicationState.error}
          application={appToDelete}
        />
      )}
    </>
  );
}

Applications.propTypes = {
  applications: PropTypes.array,
  small: PropTypes.bool,
  onCreateApplication: PropTypes.func,
  onEditApplication: PropTypes.func,
  onDeleteApplication: PropTypes.func,
};

export default Applications;
